import AccordionDetails from "@mui/material/AccordionDetails";
import {
    Step,
    StepContent,
    StepLabel,
    Stepper
} from "@mui/material";
import Box from "@mui/material/Box";
import React, {useEffect, useState} from "react";
import Button from "@mui/material/Button";
import {alphaNumericValidator, checkAllValid, numberValidator} from "../../Helpers/ValidationHelpers";
import {produce} from "immer";
import Typography from "@mui/material/Typography";
import ProductGroupQuoteDetailReview from "../Products/ProductGroupQuoteDetailReview";
import ProjectDetailSaveAsTemplatePrompt from "./ProjectDetailSaveAsTemplatePrompt";
import ProjectDetailUpdateTemplatePrompts from "./ProjectDetailUpdateTemplatePrompts";
import ProjectDetailTemplateNameField from "./ProjectDetailTemplateNameField";
import CategoryItemAutocomplete from "./Inputs/CategoryItemAutocomplete";
import CategoryItemGroupedParameter from "./Inputs/CategoryItemGroupedParameter";
import {useSnackbar} from "notistack";

function ProjectDetailRowGroupAccordionDetails({
                                                 quoteDetailId,
                                                 activeQuoteDetail,
                                                 handleDetailChange,
                                                 productGroup,
                                                 activeForm,
                                                 isUpdate, 
                                                 setExpanded,
                                                 formValidQuoteDetail,
                                                 draftQuoteDetail,
                                                 setDraftQuoteDetail,
                                                 setAllQuoteDetailsSaved,
                                                 savedQuoteDetail,
                                                 setSavedQuoteDetail, 
                                                 quoteTemplates, 
                                                 initialTemplateQuoteDetail
}) 
{
    const equal = require('fast-deep-equal');

    const [updateTemplateChecked, setUpdateTemplateChecked] = useState(false);
    const [showTemplateNewField, setShowTemplateNewField] = useState(false);
    const [showTemplateUpdateField, setShowTemplateUpdateField] = useState(false);
    const [templateNameValue, setTemplateNameValue] = useState('');
    const [error, setError] = useState(false);
    const [forceShowErrors, setForceShowErrors] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    
    const categoryList = productGroup.categories.map(category => ({
        ...category,
        productItems: category.productItems.filter(item => productGroup.allowedCategories.flatMap(category => category.allowedItems).includes(item.id))
    }));
    
    const [categoryItems, setCategoryItems] = useState(() => {
        if (isUpdate) {
            // Need to enrich cat items with dropdowns from categoryList
            return draftQuoteDetail.categoryItems.map(item => {
                const catItem = categoryList.find(category => category.id === item.productCategoryId)
                    ?.productItems.find(productItem => productItem.id === item.productItemId);
                return {
                    ...item,
                    fields: item.fields.map(field => {
                        return {
                            ...field,
                            enumOptions: catItem?.fields
                                ?.find(fld => fld.id === field.fieldId)
                                ?.enumOptions
                        }
                    })
                }
            })
        } else {
            return draftQuoteDetail.categoryItems;
        }
    });
    const [activeStep, setActiveStep] = useState(draftQuoteDetail.templateDetails ? 2 : 0);

    // Setting saved status effect
    useEffect(() => {
        
        // Set unsaved if quote detail is updated after a save
        setAllQuoteDetailsSaved(prevState => ({
            ...prevState,
            [quoteDetailId]: equal(categoryItems, savedQuoteDetail.categoryItems)
        }));

        // Show an option to update if template is edited
        if (initialTemplateQuoteDetail && !equal(categoryItems, initialTemplateQuoteDetail.categoryItems)) {
            setShowTemplateUpdateField(true);
        } else {
            setShowTemplateUpdateField(false);
        }
    }, [categoryItems])

    // Resetting fields when product is changed
    useEffect(() => {
        setForceShowErrors(false);
        if (!isUpdate) {
            setDraftQuoteDetail(produce(draftQuoteDetail, draft => {
                draft.templateName = null;
                draft.templateId = null;
            }));

            setShowTemplateNewField(false);

            if (!draftQuoteDetail.templateDetails) {
                setActiveStep(0);
                setCategoryItems([]);
            } else {
                if (checkAllValid(formValidQuoteDetail.current)) {
                    setActiveStep(2);
                } else {
                    setActiveStep(1);
                }
                
                setCategoryItems(draftQuoteDetail.categoryItems);
            }
        }
    }, [draftQuoteDetail.productGroupId, draftQuoteDetail.productItemId, draftQuoteDetail.templateDetails])

    const handleNextWithValidation = () => {
        if (!checkAllValid(formValidQuoteDetail.current)) {
            // Force showing validation errors on all fields
            setForceShowErrors(true);
            return;
        }
        setForceShowErrors(false);
        handleNext();
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleNextWithCategoryValidation = () => {
        if (categoryItems.length === 0) {
            enqueueSnackbar("Please choose at least one item!", {
                variant: "warning",
                autoHideDuration: 3000,
            });
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    }

    const handleBack = (step) => {
        setForceShowErrors(false);
        setActiveStep((prevActiveStep) => (step !== undefined ? step : prevActiveStep - 1));
    };

    const handleSaveGroupDetail = () => {
        setActiveStep(0);
        setExpanded(false);
        let quoteDetailToSet;
        // Update template
        if (!!draftQuoteDetail.templateName && !!draftQuoteDetail.templateId) {
            quoteDetailToSet =  {
                ...draftQuoteDetail,
                categoryItems: categoryItems,
                templateId: draftQuoteDetail?.templateDetails?.templateId,
                templateName: draftQuoteDetail?.templateDetails?.templateName,
            };
            // Create Template
        } else if (!!draftQuoteDetail.templateName) {
            quoteDetailToSet = {
                ...draftQuoteDetail,
                categoryItems: categoryItems,
                templateName: templateNameValue,
                templateId: null
            };
            // No template creation/update
        }  else {
            quoteDetailToSet = {
                ...draftQuoteDetail,
                categoryItems: categoryItems,
                templateName: null,
                templateId: null
            };
        }
        handleDetailChange(quoteDetailId, quoteDetailToSet);
        setAllQuoteDetailsSaved(prevState => ({
            ...prevState,
            [quoteDetailId ?? activeQuoteDetail.id]: true
        }));
        setSavedQuoteDetail(quoteDetailToSet);
    }
    
    const handleTemplateNameChange = (e) => {
        const { value: newValue } = e.target;
        const errorMessage = alphaNumericValidator(newValue)
            || (quoteTemplates.some(x => x.name === newValue)
                ? `Template '${newValue}' already exists.`
                : false)
            || (activeForm?.quoteDetails
                ?.filter(detail => detail.tempId !== quoteDetailId || detail.id !== quoteDetailId )
                ?.some(x => x.templateName === newValue)
                ? `Template '${newValue}' is already being saved in another quote item.`
                : false);
        setError(errorMessage);
        formValidQuoteDetail.current.templateName = !errorMessage;
        setTemplateNameValue(newValue);
        setDraftQuoteDetail(produce(draftQuoteDetail, draft => {
            draft.templateName = newValue;
        }));
    }

    const extractGroupedFields = () => {
        const draft = {};

        // Keep track of valid field names and their IDs based on current `categoryItems`
        const validFields = new Set();
        const validFieldIds = {};

        // Clean up formValidQuoteDetail.current - remove entries not in current categoryItems
        const currentCategoryKeys = new Set();
        categoryItems?.forEach((item) => {
            currentCategoryKeys.add(`${item.productCategoryId}-${item.productItemId}`);
        });

        // Remove obsolete category/product entries from formValidQuoteDetail
        Object.keys(formValidQuoteDetail.current).forEach((categoryId) => {
            if (categoryId === 'id' || categoryId === 'templateName') return; // Skip special keys

            if (typeof formValidQuoteDetail.current[categoryId] === 'object') {
                Object.keys(formValidQuoteDetail.current[categoryId]).forEach((productItemId) => {
                    if (!currentCategoryKeys.has(`${categoryId}-${productItemId}`)) {
                        delete formValidQuoteDetail.current[categoryId][productItemId];
                    }
                });

                // If category has no items left, remove it entirely
                if (Object.keys(formValidQuoteDetail.current[categoryId]).length === 0) {
                    delete formValidQuoteDetail.current[categoryId];
                }
            }
        });

        categoryItems?.forEach((item) => {
            item?.fields?.forEach((field) => {
                validFields.add(field.name);

                // Collect valid `fieldId`s for this field
                if (!validFieldIds[field.name]) {
                    validFieldIds[field.name] = new Set();
                }
                validFieldIds[field.name].add(field.fieldId);

                // Ensure `draft[field.name]` is initialized before accessing
                if (!draft[field.name]) {
                    draft[field.name] = {
                        details: {
                            name: field.name,
                            displayName: field.displayName,
                            enumOptions: field.enumOptions,
                            type: field.type,
                            uom: field.uom,
                        },
                        value: field.value ?? "",
                        ids: [],
                    };
                }

                // Avoid adding duplicate IDs to `draft[field.name].ids`
                if (!draft[field.name].ids.some((idObject) => idObject.productItemId === item.productItemId)) {
                    draft[field.name].ids.push({
                        fieldId: field.fieldId,
                        categoryId: item.productCategoryId,
                        productItemId: item.productItemId,
                        productItemName: item.productItemName,
                    });
                }

                // Initialize `formValidQuoteDetail` object structure before assigning
                if (!formValidQuoteDetail.current[item.productCategoryId]) {
                    formValidQuoteDetail.current[item.productCategoryId] = {};
                }
                if (!formValidQuoteDetail.current[item.productCategoryId][item.productItemId]) {
                    formValidQuoteDetail.current[item.productCategoryId][item.productItemId] = {};
                }
                formValidQuoteDetail.current[item.productCategoryId][item.productItemId][field.fieldId] = !!field.value;
            });
        });

        return draft;
    };

    const [groupedFields, setGroupedFields] = useState({});

    useEffect(() => {
        setGroupedFields(extractGroupedFields());
    }, [categoryItems]);

    const { commonFields, itemSpecificFields } = Object.entries(groupedFields).reduce(
        (result, [fieldName, field]) => {
            if (field.ids.length > 1) {
                result.commonFields.push([fieldName, field]);
            } else {
                result.itemSpecificFields.push([fieldName, field]);
            }
            return result;
        },
        { commonFields: [], itemSpecificFields: [] }
    );

    commonFields.sort(([, fieldA], [, fieldB]) =>
        fieldA.details.displayName.localeCompare(fieldB.details.displayName)
    );
    
    itemSpecificFields.sort(([, fieldA], [, fieldB]) =>
        fieldA.details.displayName.localeCompare(fieldB.details.displayName)
    );
    
    const handleValueChange = (field, newValue) => {
        setGroupedFields((prevState) =>
            produce(prevState, (draft) => {
                draft[field.name].value = newValue;
            })
        );
    }
    
    useEffect(() => {
        setCategoryItems(prevState => produce(prevState, draft => {
            draft.forEach(item => {
                item.fields.forEach(field => {
                    if (groupedFields[field.name]?.value) {
                        field.value = groupedFields[field.name]?.value;
                    }
                })
            })
        }))
    }, [groupedFields])

    return (
        <AccordionDetails>
            <Stepper activeStep={activeStep} orientation="vertical">
                <Step>
                    <StepLabel
                        onClick={() => handleBack(0)}
                        style={{cursor: activeStep > 0 ? 'pointer' : "default"}}
                        className={activeStep > 0 ? "hyperlink" : ""}>Choose items</StepLabel>
                    <StepContent>
                        {
                            categoryList.map((category, index) => (
                                <Box key={index}>
                                    <Typography variant="h6">{category.name}</Typography>
                                    <CategoryItemAutocomplete 
                                        category={category}
                                        categoryItems={categoryItems}
                                        setCategoryItems={setCategoryItems}
                                        formValidQuoteDetail={formValidQuoteDetail}
                                        width={{xs: "250px !important", md: "300px !important",}} />
                                </Box>
                            ))
                        }
                        <Box sx={{ my: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleNextWithCategoryValidation}
                                sx={{ mt: 1, mr: 1 }}
                            >
                                Continue
                            </Button>
                        </Box>
                    </StepContent>
                </Step>
                <Step>
                    <StepLabel
                        onClick={() => { if (activeStep > 1) { handleBack(); } }}
                        style={{cursor: activeStep > 1 ? 'pointer' : "default"}}
                        className={activeStep > 1 ? "hyperlink" : ""}>Configure parameters</StepLabel>
                    <StepContent>
                        <Box>
                            {commonFields.length > 0 && (
                                <>
                                    <Typography variant="h6">Common</Typography>
                                    {commonFields.map(([fieldName, field], index) => (
                                        <Box
                                            key={index}
                                            display="flex"
                                            justifyContent="left"
                                            sx={{ pl: 2, my: 1 }}
                                        >
                                            <CategoryItemGroupedParameter
                                                id={index}
                                                fieldValue={field.value}
                                                onChange={(isValid) => {
                                                    field.ids.forEach((idObject) => {
                                                        if (formValidQuoteDetail.current[idObject.categoryId]?.[idObject.productItemId]) {
                                                            formValidQuoteDetail.current[idObject.categoryId][idObject.productItemId][idObject.fieldId] = isValid;
                                                        }
                                                    });
                                                }}
                                                field={field.details}
                                                validator={(field.details.type === "Number" || field.type === 2) ? numberValidator : alphaNumericValidator}
                                                handleValueChange={handleValueChange}
                                                forceShowError={forceShowErrors}
                                                errorInitial={draftQuoteDetail.templateDetails &&
                                                    field.ids.some((idObject) =>
                                                        formValidQuoteDetail.current[idObject.categoryId]?.[idObject.productItemId]?.[idObject.fieldId]
                                                    )
                                                }
                                            />
                                        </Box>
                                    ))}
                                </>
                            )}
                            {
                                categoryItems.map((item, index) => {
                                    const relevantFields = item.fields.filter(field =>
                                        itemSpecificFields.some(itemSpecificField => itemSpecificField[0] === field.name)
                                    );

                                    return (
                                        relevantFields.length > 0 && (
                                            <div key={index}>
                                                <Typography variant="h6">{item.productItemName}</Typography>
                                                {relevantFields.map((field, fieldIndex) => (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="left"
                                                        sx={{ pl: 2, my: 1 }}
                                                        key={fieldIndex}
                                                    >
                                                        <CategoryItemGroupedParameter
                                                            id={fieldIndex}
                                                            field={field}
                                                            fieldValue={item.fields?.find(x => x.fieldId === field.fieldId)?.value}
                                                            onChange={(isValid) => {
                                                                if (formValidQuoteDetail.current[item.productCategoryId]?.[item.productItemId]) {
                                                                    formValidQuoteDetail.current[item.productCategoryId][item.productItemId][field.fieldId] = isValid;
                                                                }
                                                            }}
                                                            validator={(field.type === "Number" || field.type === 2) ? numberValidator : alphaNumericValidator}
                                                            handleValueChange={handleValueChange}
                                                            forceShowError={forceShowErrors}
                                                            errorInitial={draftQuoteDetail.templateDetails && formValidQuoteDetail.current[item.productCategoryId]?.[item.productItemId]?.[field.fieldId]}
                                                        />
                                                    </Box>
                                                ))}
                                            </div>
                                        )
                                    );
                                })
                            }
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleNextWithValidation}
                                    sx={{ mt: 1, mr: 1 }}
                                >
                                    Continue
                                </Button>
                                <Button
                                    onClick={() => handleBack(0)}
                                    sx={{ mt: 1, mr: 1 }}
                                >
                                    Back
                                </Button>
                            </Box>
                        </Box>
                    </StepContent>
                </Step>
                <Step>
                    <StepLabel>Review</StepLabel>
                    <StepContent>
                        <Box
                            display="flex"
                            justifyContent="left"
                            sx={{ my: 2 }}
                        >
                            <ProductGroupQuoteDetailReview
                                productGroupName={productGroup.name}
                                categoryItems={categoryItems} />
                        </Box>
                        {
                            !isUpdate &&
                            <>
                                <ProjectDetailUpdateTemplatePrompts
                                    draftQuoteDetail={draftQuoteDetail}
                                    showTemplateNewField={showTemplateNewField}
                                    setDraftQuoteDetail={setDraftQuoteDetail}
                                    formValidQuoteDetail={formValidQuoteDetail}
                                    setShowTemplateNewField={setShowTemplateNewField}
                                    showTemplateUpdateField={showTemplateUpdateField}
                                    updateTemplateChecked={updateTemplateChecked}
                                    setUpdateTemplateChecked={setUpdateTemplateChecked}
                                />
                                    <Box display="flex"
                                    justifyContent="left"
                                    alignItems="center"
                                    sx={{mb: 2}}
                                >
                                <ProjectDetailSaveAsTemplatePrompt
                                    showTemplateNewField={showTemplateNewField}
                                    setShowTemplateNewField={setShowTemplateNewField}
                                    draftQuoteDetail={draftQuoteDetail}
                                    setDraftQuoteDetail={setDraftQuoteDetail}
                                    formValidQuoteDetail={formValidQuoteDetail}
                                />
                            </Box>
                            <ProjectDetailTemplateNameField
                                draftQuoteDetail={draftQuoteDetail}
                                showTemplateNewField={showTemplateNewField}
                                handleTemplateNameChange={handleTemplateNameChange}
                                error={error}
                            />
                        </>}
                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveGroupDetail}
                                sx={{ mt: 1, mr: 1 }}
                            >
                                Save
                            </Button>
                            <Button
                                onClick={() => handleBack()}
                                sx={{ mt: 1, mr: 1 }}
                            >
                                Back
                            </Button>
                        </Box>
                    </StepContent>
                </Step>
            </Stepper>
        </AccordionDetails>
    )
}

export default ProjectDetailRowGroupAccordionDetails;